/**
 * @file Anything that can run on every page.
 */

import debounce from 'just-debounce-it';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollSmoother } from 'gsap/ScrollSmoother';

/**
 * Global functions.
 *
 * Runs component js and any global tasks.
 */
const vGlobal = () => {
	gsap.registerPlugin(ScrollTrigger, ScrollSmoother);
	const smoother = ScrollSmoother.create({
		smooth: 1,
		effects: true,
		smoothTouch: 0.1,
	});

	import('../components/header/header').then(({ default: vHeader }) => {
		vHeader();
	});

	import('../components/menu/menu').then(({ default: vMenu }) => {
		vMenu();
	});

	import('../components/toggle-page-theme/toggle-page-theme').then(
		({ default: vTogglePageTheme }) => {
			vTogglePageTheme();
		},
	);

	if (document.querySelector('.v-hero')) {
		import('../components/hero/hero').then(({ default: vHero }) => {
			vHero();
		});
	}

	if (document.querySelector('.v-draw-border')) {
		import('../components/draw-border/draw-border').then(
			({ default: vDrawBorder }) => {
				vDrawBorder();
			},
		);
	}

	if (document.querySelector('.v-image-slider')) {
		import('../components/image-slider/image-slider').then(
			({ default: vImageSlider }) => {
				vImageSlider();
			},
		);
	}

	if (document.querySelector('.v-slider')) {
		import('../components/slider/slider').then(({ default: vSlider }) => {
			vSlider();
		});
	}

	if (document.querySelector('.v-stats-quote')) {
		import('../components/stats-quote/stats-quote').then(
			({ default: vStatsQuote }) => {
				vStatsQuote();
			},
		);
	}

	let isInitialPageLoad = true;
	document.addEventListener('facetwp-loaded', () => {
		// Only scroll to results after filtering, not on initial page load
		if (!isInitialPageLoad) {
			const headerHeight =
				document.querySelector('.v-header__sticky')?.offsetHeight || 80;
			setTimeout(() => {
				smoother.scrollTo('.facetwp-template', true, `top ${headerHeight}px`);
			}, 10);
		}
		isInitialPageLoad = false;
	});

	/**
	 * Get scrollbar width.
	 * @returns {number} Scrollbar width.
	 */
	function getScrollbarWidth() {
		const outer = document.createElement('div');
		outer.style.visibility = 'hidden';
		outer.style.overflow = 'scroll';
		document.body.appendChild(outer);

		const inner = document.createElement('div');
		outer.appendChild(inner);

		const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;

		outer.parentNode.removeChild(outer);

		return scrollbarWidth;
	}

	document.documentElement.style.setProperty(
		'--scrollbar-width',
		`${getScrollbarWidth()}px`,
	);
	window.addEventListener(
		'resize',
		debounce(() => {
			document.documentElement.style.setProperty(
				'--scrollbar-width',
				`${getScrollbarWidth()}px`,
			);
		}),
		100,
	);

	const selects = document.querySelectorAll('select');
	[...selects].forEach((select) => {
		const selectParent = select.parentNode;
		if (selectParent.classList.contains('v-form__select')) {
			return;
		}

		const wrapper = document.createElement('span');
		const arrow = document.createElement('span');
		wrapper.classList.add('v-form__select');
		arrow.classList.add('v-form__select-arrow');
		selectParent.insertBefore(wrapper, select);
		wrapper.appendChild(select);
		wrapper.appendChild(arrow);
		select.classList.add('v-form__select-field');
	});

	const tableWrappers = document.querySelectorAll('.v-table-wrapper');
	/**
	 * Toggle scroll indicator classes on table wrappers.
	 * @param {Element} tableWrapper - Table wrapper.
	 * @param {Element} tableInner - Scrollable inner table wrapper.
	 */
	const tableToggleClasses = (tableWrapper, tableInner) => {
		if (tableInner.scrollLeft > 2) {
			tableWrapper.classList.add('v-table-wrapper--scrollable-start');
		} else {
			tableWrapper.classList.remove('v-table-wrapper--scrollable-start');
		}

		if (
			tableInner.scrollLeft + tableInner.clientWidth <
			tableInner.scrollWidth - 2
		) {
			tableWrapper.classList.add('v-table-wrapper--scrollable-end');
		} else {
			tableWrapper.classList.remove('v-table-wrapper--scrollable-end');
		}
	};

	tableWrappers.forEach((tableWrapper) => {
		const inner = tableWrapper.querySelector('.v-table-wrapper__inner');
		inner.addEventListener(
			'scroll',
			tableToggleClasses.bind(null, tableWrapper, inner),
			{ passive: true },
		);

		const observer = new ResizeObserver(() => {
			tableToggleClasses(tableWrapper, inner);
		});
		observer.observe(tableWrapper, { box: 'border-box' });
		tableToggleClasses(tableWrapper, inner);
	});

	const floatingLabels = document.querySelectorAll('.v-form__floating-label');
	if (floatingLabels.length) {
		const floatingInputs = [...floatingLabels].map((l) => {
			const wrap = l.parentElement;
			return wrap.querySelector('input, select, textarea');
		});

		[...floatingInputs].forEach((field, i) => {
			field.addEventListener('focusin', () => {
				floatingLabels[i].classList.add('v-form__floating-label--active');
			});

			field.addEventListener('focusout', () => {
				if (!field.value) {
					floatingLabels[i].classList.remove('v-form__floating-label--active');
				}
			});

			field.addEventListener('change', () => {
				if (!field.value) {
					floatingLabels[i].classList.remove('v-form__floating-label--active');
				}
			});

			const fieldObserver = new MutationObserver((e) => {
				if (!field.value) {
					floatingLabels[i].classList.remove('v-form__floating-label--active');
				}
			});
			fieldObserver.observe(field, { attributes: true });
		});
	}

	const pageWrapper = document.querySelector('.v-page-wrapper');

	window.addEventListener('vOverlayOpening', (e) => {
		// const { scrollY } = e?.detail || 0;
		// document.body.style.setProperty('--scroll-top', `${-scrollY}px`);
		pageWrapper.setAttribute('inert', '');
		// pageWrapper.scrollTo(0, scrollY);
	});

	// window.addEventListener('vOverlayClosing', () => {});

	window.addEventListener('vOverlayClosed', () => {
		pageWrapper.toggleAttribute('inert', false);
		// pageWrapper.scrollTo({
		// left: 0,
		// top: 0,
		// behavior: 'instant',
		// });
		// window.scrollTo({
		// left: 0,
		// top:
		// parseInt(document.body.style.getPropertyValue('--scroll-top'), 10) * -1,
		// behavior: 'instant',
		// });
	});

	/* Promise.all([import('gsap'), import('gsap/ScrollTrigger')]).then(
		([{ gsap }, { ScrollTrigger }]) => {
			gsap.registerPlugin(ScrollTrigger);
			window.addEventListener('vOverlayOpening', (e) => {
				const { scrollY } = e.detail;
				ScrollTrigger.config({
					autoRefreshEvents: 'visibilitychange,DOMContentLoaded,load',
				});
				ScrollTrigger.getAll().forEach((trigger) => {
					trigger.disable(false);
					if (!trigger.pin) return;

					if (gsap.getProperty(trigger.pin, 'position') === 'fixed') {
						gsap.set(trigger.pin, {
							top: gsap.getProperty(trigger.pin, 'top') + scrollY,
						});
					}

					if (
						gsap.getProperty(trigger.pin, 'width') === pageWrapper.offsetWidth
					) {
						let targets = [trigger.pin, trigger.spacer].filter(Boolean);
						gsap.set(targets, {
							width: '100%',
							maxWidth: '100%',
						});
					}
				});
			});

			window.addEventListener('vOverlayClosed', () => {
				ScrollTrigger.config({
					autoRefreshEvents: 'visibilitychange,DOMContentLoaded,load,resize',
				});
				ScrollTrigger.getAll().forEach((trigger) => {
					trigger.enable(false, true);
				});
			});
		},
	); */

	/**
	 * Global Window Load.
	 *
	 * Runs polyfills on page load.
	 */
	async function onWindowLoad() {
		const body = document.querySelector('body');
		if (!body.classList.contains('admin-bar')) {
			const { listen } = await import('quicklink');

			listen({
				ignores: [
					(url) => {
						const cleanUrl = url.replace(/#.*/, '');
						return cleanUrl === window.location.href;
					},
					/.*\/wp-admin\/.*/,
				],
			});
		}
	}

	if (document.readyState === 'complete') {
		onWindowLoad();
	} else {
		window.addEventListener('load', onWindowLoad);
	}
};

if (document.readyState !== 'loading') {
	vGlobal();
} else {
	document.addEventListener('DOMContentLoaded', vGlobal);
}
